import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const formatDuration = (isoDuration) => {
  if (!isoDuration) return '';
  const match = isoDuration?.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  if (!match) return isoDuration;
  const hours = match?.[1] ? `${match?.[1]}h` : '';
  const mins = match?.[2] ? `${match?.[2]}m` : '';
  return `${hours} ${mins}`?.trim();
};

const formatTime = (isoString) => {
  if (!isoString) return '--:--';
  const date = new Date(isoString);
  return date?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
};

const formatDate = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date?.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const SliceDisplay = ({ slice }) => {
  const stops = slice?.stops || 0;
  const stopLabel = stops === 0 ? 'Nonstop' : stops === 1 ? '1 Stop' : `${stops} Stops`;
  const stopColor = stops === 0 ? 'text-success' : stops === 1 ? 'text-warning' : 'text-destructive';

  return (
    <div className="flex items-center gap-4 flex-1">
      {/* Departure */}
      <div className="text-center min-w-[60px]">
        <p className="text-xl font-semibold text-foreground">{formatTime(slice?.departureAt)}</p>
        <p className="text-xs text-muted-foreground font-medium">{slice?.origin}</p>
        <p className="text-xs text-muted-foreground">{formatDate(slice?.departureAt)}</p>
      </div>

      {/* Flight Line */}
      <div className="flex-1 flex flex-col items-center gap-1">
        <p className="text-xs text-muted-foreground">{formatDuration(slice?.duration)}</p>
        <div className="relative w-full flex items-center">
          <div className="h-px flex-1 bg-border"></div>
          <Icon name="Plane" size={14} className="text-muted-foreground mx-1" />
          <div className="h-px flex-1 bg-border"></div>
        </div>
        <p className={`text-xs font-medium ${stopColor}`}>{stopLabel}</p>
      </div>

      {/* Arrival */}
      <div className="text-center min-w-[60px]">
        <p className="text-xl font-semibold text-foreground">{formatTime(slice?.arrivalAt)}</p>
        <p className="text-xs text-muted-foreground font-medium">{slice?.destination}</p>
        <p className="text-xs text-muted-foreground">{formatDate(slice?.arrivalAt)}</p>
      </div>
    </div>
  );
};

const ConditionBadge = ({ allowed, label, penalty }) => (
  <div className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-lg ${
    allowed ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
  }`}>
    <Icon name={allowed ? 'CheckCircle' : 'XCircle'} size={12} />
    <span>{label}</span>
    {allowed && penalty && <span className="opacity-70">({penalty})</span>}
  </div>
);

const FlightOfferCard = ({ offer, onSelect }) => {
  const [expanded, setExpanded] = useState(false);

  const isRoundTrip = offer?.slices?.length > 1;

  return (
    <div className="bg-card border border-border rounded-2xl shadow-card hover:shadow-dropdown transition-shadow duration-200 overflow-hidden">
      <div className="p-5">
        <div className="flex items-start gap-4">
          {/* Airline Logo / Name */}
          <div className="flex-shrink-0 flex flex-col items-center gap-1 min-w-[64px]">
            {offer?.airline?.logoUrl ? (
              <img
                src={offer?.airline?.logoUrl}
                alt={`${offer?.airline?.name} logo`}
                className="h-8 w-8 object-contain rounded"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
                <Icon name="Plane" size={16} className="text-muted-foreground" />
              </div>
            )}
            <p className="text-xs text-muted-foreground text-center leading-tight">{offer?.airline?.name || offer?.airline?.iataCode}</p>
          </div>

          {/* Slices */}
          <div className="flex-1 space-y-3">
            {offer?.slices?.map((slice, idx) => (
              <div key={slice?.id || idx}>
                {isRoundTrip && (
                  <p className="text-xs font-medium text-muted-foreground mb-1">
                    {idx === 0 ? 'Outbound' : 'Return'}
                  </p>
                )}
                <SliceDisplay slice={slice} />
              </div>
            ))}
          </div>

          {/* Price & CTA */}
          <div className="flex-shrink-0 flex flex-col items-end gap-2 min-w-[110px]">
            <div className="text-right">
              <p className="text-2xl font-semibold text-foreground">
                {offer?.totalCurrency} {parseFloat(offer?.totalAmount)?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-muted-foreground">per person total</p>
            </div>
            <Button
              size="sm"
              onClick={() => onSelect?.(offer)}
              className="text-white w-full"
              style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B, #E0528A)' }}
            >
              Select
            </Button>
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
            >
              {expanded ? 'Hide details' : 'View details'}
              <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={12} />
            </button>
          </div>
        </div>

        {/* Conditions Row */}
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border">
          <ConditionBadge
            allowed={offer?.conditions?.refundable}
            label="Refundable"
            penalty={offer?.conditions?.refundPenalty ? `${offer?.totalCurrency} ${offer?.conditions?.refundPenalty}` : null}
          />
          <ConditionBadge
            allowed={offer?.conditions?.changeable}
            label="Changeable"
            penalty={offer?.conditions?.changePenalty ? `${offer?.totalCurrency} ${offer?.conditions?.changePenalty}` : null}
          />
          {offer?.expiresAt && (
            <div className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-lg bg-warning/10 text-warning ml-auto">
              <Icon name="Clock" size={12} />
              <span>Expires {new Date(offer?.expiresAt)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          )}
        </div>
      </div>

      {/* Expanded Segment Details */}
      {expanded && (
        <div className="border-t border-border bg-muted/30 px-5 py-4 animate-fade-in">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Flight Segments</p>
          {offer?.slices?.map((slice, sIdx) => (
            <div key={slice?.id || sIdx} className="mb-4 last:mb-0">
              {isRoundTrip && (
                <p className="text-xs font-medium text-foreground mb-2">{sIdx === 0 ? '✈ Outbound' : '✈ Return'}</p>
              )}
              <div className="space-y-2">
                {slice?.segments?.map((seg, segIdx) => (
                  <div key={seg?.id || segIdx} className="flex items-center gap-3 text-sm bg-card rounded-xl px-4 py-3 border border-border">
                    <div className="flex-shrink-0 text-center min-w-[40px]">
                      <p className="font-semibold text-foreground">{formatTime(seg?.departureAt)}</p>
                      <p className="text-xs text-muted-foreground">{seg?.origin}</p>
                    </div>
                    <div className="flex-1 flex flex-col items-center">
                      <p className="text-xs text-muted-foreground">{formatDuration(seg?.duration)}</p>
                      <div className="w-full h-px bg-border my-1"></div>
                      <p className="text-xs font-medium text-foreground">{seg?.flightNumber}</p>
                    </div>
                    <div className="flex-shrink-0 text-center min-w-[40px]">
                      <p className="font-semibold text-foreground">{formatTime(seg?.arrivalAt)}</p>
                      <p className="text-xs text-muted-foreground">{seg?.destination}</p>
                    </div>
                    {seg?.aircraft && (
                      <div className="flex-shrink-0 text-right">
                        <p className="text-xs text-muted-foreground">{seg?.aircraft}</p>
                        <p className="text-xs text-muted-foreground">{seg?.carrier}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FlightOfferCard;
